import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import auditService from '../../services/auditService';
import userService from '../../services/userService';
import { getWarehouses } from '../../services/warehousesService';
import Toast from '../../components/Common/Toast';

function buildRecipientState(users, rows) {
  const state = {};
  (users || []).forEach((user) => {
    state[user.id] = {
      assigned: false,
      notify_kardex: true,
      notify_inventory_count: true,
    };
  });
  (rows || []).forEach((row) => {
    if (!row.user_id) return;
    state[row.user_id] = {
      assigned: true,
      notify_kardex: Boolean(row.notify_kardex),
      notify_inventory_count: Boolean(row.notify_inventory_count),
    };
  });
  return state;
}

export default function AuditNotificationRecipientsPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [recipientState, setRecipientState] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const activeUsers = useMemo(
    () => (users || []).filter((u) => u.is_active).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [users],
  );

  useEffect(() => {
    let cancelled = false;
    const loadBase = async () => {
      setLoading(true);
      try {
        const [whRes, usersRes] = await Promise.all([
          getWarehouses({ active_only: true }),
          userService.getUsers().catch(() => ({ success: false })),
        ]);
        if (cancelled) return;
        if (whRes.success) {
          const list = whRes.data || [];
          setWarehouses(list);
          if (list.length > 0) {
            setWarehouseId(String(list[0].id));
          }
        }
        if (usersRes.success) {
          setUsers(usersRes.data || []);
          setCanManageUsers(true);
        } else {
          setCanManageUsers(false);
        }
      } catch {
        if (!cancelled) {
          setToast({ isOpen: true, message: 'Error al cargar datos', type: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadBase();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!warehouseId || !canManageUsers) return;
    let cancelled = false;
    auditService
      .getNotificationRecipients(warehouseId)
      .then((res) => {
        if (cancelled) return;
        if (res.success) {
          setRecipientState(buildRecipientState(activeUsers, res.data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setToast({ isOpen: true, message: 'Error al cargar destinatarios', type: 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [warehouseId, canManageUsers, activeUsers]);

  const updateUser = (userId, patch) => {
    setRecipientState((prev) => ({
      ...prev,
      [userId]: { ...prev[userId], ...patch },
    }));
  };

  const toggleAssigned = (userId, assigned) => {
    updateUser(userId, {
      assigned,
      notify_kardex: assigned ? (recipientState[userId]?.notify_kardex ?? true) : false,
      notify_inventory_count: assigned ? (recipientState[userId]?.notify_inventory_count ?? true) : false,
    });
  };

  const save = async () => {
    if (!warehouseId) return;
    setSaving(true);
    try {
      const recipients = activeUsers
        .filter((user) => recipientState[user.id]?.assigned)
        .map((user) => ({
          user_id: user.id,
          notify_kardex: Boolean(recipientState[user.id]?.notify_kardex),
          notify_inventory_count: Boolean(recipientState[user.id]?.notify_inventory_count),
        }));
      const res = await auditService.updateNotificationRecipients(warehouseId, recipients);
      if (res.success) {
        setRecipientState(buildRecipientState(activeUsers, res.data));
        setToast({ isOpen: true, message: 'Destinatarios guardados', type: 'success' });
      }
    } catch {
      setToast({ isOpen: true, message: 'No se pudieron guardar los destinatarios', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <nav className="text-sm text-gray-600 mb-3">
        <Link to="/audit/kardex" className="hover:text-teal-600">Auditoría</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Destinatarios por almacén</span>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Destinatarios de notificaciones</h1>
        <p className="text-gray-600 mt-1 text-sm">
          Asigne usuarios por almacén. Solo ellos verán la campana y recibirán alertas según el tipo configurado.
        </p>
        <Link to="/audit/rules" className="inline-flex mt-2 text-sm text-teal-700 hover:underline">
          Ver y configurar reglas de auditoría →
        </Link>
      </div>

      <div className="mb-6 p-4 bg-gray-50 border rounded-lg text-sm text-gray-600">
        Las reglas definen <strong>qué</strong> se detecta. Aquí define <strong>quién</strong> recibe cada tipo de alerta por almacén.
      </div>

      {!canManageUsers && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          Solo un administrador puede modificar destinatarios. Si necesita recibir alertas, pida al administrador que lo asigne aquí.
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : !canManageUsers ? (
        <div className="p-4 bg-white border rounded-lg text-sm text-gray-600">
          No tiene permiso para listar usuarios. Solicite a un administrador que configure los destinatarios en esta pantalla.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <label className="text-sm">
              <span className="block text-gray-700 mb-1">Almacén</span>
              <select
                className="border rounded px-3 py-2 text-sm min-w-[220px]"
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                disabled={!canManageUsers}
              >
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>{w.name || w.code}</option>
                ))}
              </select>
            </label>
            {canManageUsers && (
              <button
                type="button"
                onClick={save}
                disabled={saving || !warehouseId}
                className="px-4 py-2 bg-teal-600 text-white rounded text-sm disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Guardar destinatarios'}
              </button>
            )}
          </div>

          <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3">Usuario</th>
                  <th className="px-4 py-3 text-center">Asignado</th>
                  <th className="px-4 py-3 text-center">Alertas kardex</th>
                  <th className="px-4 py-3 text-center">Alertas inventario</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {activeUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-gray-500">No hay usuarios activos.</td>
                  </tr>
                ) : (
                  activeUsers.map((user) => {
                    const row = recipientState[user.id] || {
                      assigned: false,
                      notify_kardex: true,
                      notify_inventory_count: true,
                    };
                    return (
                      <tr key={user.id}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{user.name}</p>
                          <p className="text-gray-500 text-xs">{user.email}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={row.assigned}
                            disabled={!canManageUsers}
                            onChange={(e) => toggleAssigned(user.id, e.target.checked)}
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={row.assigned && row.notify_kardex}
                            disabled={!canManageUsers || !row.assigned}
                            onChange={(e) => updateUser(user.id, { notify_kardex: e.target.checked })}
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={row.assigned && row.notify_inventory_count}
                            disabled={!canManageUsers || !row.assigned}
                            onChange={(e) => updateUser(user.id, { notify_inventory_count: e.target.checked })}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
}
